import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase';
import './SchoolLogin.css';

function SchoolLogin() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear errors when user starts typing
    if (error) {
      setError('');
    }
    if (name === 'email' && emailError) {
      setEmailError('');
    }
    if (name === 'password' && passwordError) {
      setPasswordError('');
    }
  };


  // Check for saved credentials on component mount
  useEffect(() => {
    const checkSavedCredentials = async () => {
      // Check if user was explicitly logged out
      const wasLoggedOut = sessionStorage.getItem('schoolLoggedOut');
      if (wasLoggedOut) {
        // User was logged out, don't auto-login
        sessionStorage.removeItem('schoolLoggedOut');
        return;
      }
      
      const savedEmail = localStorage.getItem('schoolRememberEmail');
      const savedPassword = localStorage.getItem('schoolRememberPassword');
      
      if (savedEmail && savedPassword) {
        setFormData({
          email: savedEmail,
          password: savedPassword
        });
        setRememberMe(true);
        
        // Auto-login with saved credentials
        try {
          const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
            email: savedEmail,
            password: savedPassword
          });

          if (!authError && authData?.user?.id) {
            // Get school data
            const { data: schoolData, error: schoolError } = await supabase
              .from('schools')
              .select('*')
              .eq('auth_user_id', authData.user.id)
              .single();

            if (!schoolError && schoolData) {
              // Store school session
              localStorage.setItem('schoolAuthenticated', 'true');
              localStorage.setItem('schoolData', JSON.stringify(schoolData));
              localStorage.setItem('supabaseAuthToken', authData.session.access_token);
              
              // Redirect to school dashboard
              navigate('/school/dashboard');
              return;
            }
          }
        } catch (err) {
          console.log('Auto-login failed, showing login form');
        }
      }
    };

    checkSavedCredentials();
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Use Supabase Auth to sign in
      console.log('Attempting Supabase Auth sign in for:', formData.email);
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email: formData.email,
        password: formData.password
      });

      if (authError) {
        console.error('Supabase Auth error:', authError);
        if (authError.message.includes('Invalid login credentials')) {
          setError('Invalid email or password');
        } else if (authError.message.includes('Email not confirmed')) {
          setError('Please check your email and confirm your account before logging in.');
        } else {
          setError('Login failed. Please try again.');
        }
        setLoading(false);
        return;
      }

      console.log('Supabase Auth sign in successful:', authData);

      // Get school data from schools table using the auth user ID
      const { data: schoolData, error: schoolError } = await supabase
        .from('schools')
        .select('*')
        .eq('auth_user_id', authData.user.id)
        .single();

      if (schoolError || !schoolData) {
        console.error('Error fetching school data:', schoolError);
        setError('School data not found. Please contact administrator.');
        setLoading(false);
        return;
      }

      // Store school session
      localStorage.setItem('schoolAuthenticated', 'true');
      localStorage.setItem('schoolData', JSON.stringify(schoolData));
      localStorage.setItem('supabaseAuthToken', authData.session.access_token);
      
      // Save credentials if "Remember Me" is checked
      if (rememberMe) {
        localStorage.setItem('schoolRememberEmail', formData.email);
        localStorage.setItem('schoolRememberPassword', formData.password);
      } else {
        // Clear saved credentials if "Remember Me" is unchecked
        localStorage.removeItem('schoolRememberEmail');
        localStorage.removeItem('schoolRememberPassword');
      }
      
      // Redirect to school dashboard
      navigate('/school/dashboard');
      
    } catch (err) {
      console.error('Login error:', err);
      setError('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailBlur = () => {
    if (formData.email && !validateEmail(formData.email)) {
      setEmailError('Please enter a valid email address');
    } else {
      setEmailError('');
    }
  };

  const handlePasswordBlur = () => {
    // No password length restriction
    setPasswordError('');
  };

  return (
    <div className="school-login-container">
      <div className="school-login-card">
        <div className="school-login-header">
          <img 
            src="https://gjamvfwvqlizginqdfhd.supabase.co/storage/v1/object/public/transport-uploads/WhatsApp%20Image%202025-11-21%20at%2011.57.28%20AM%20(1).jpeg" 
            alt="Nexy App Logo" 
            className="school-login-logo"
          />
          <h1>School Login</h1>
          <p>Access your school portal</p>
        </div>
        
        <form onSubmit={handleSubmit} className="school-login-form">
          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <div className="input-wrapper">
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
                onBlur={handleEmailBlur}
                placeholder="superadmin@example.com"
                className={emailError || error ? 'error' : ''}
                style={{ backgroundColor: '#F9FAFB', outline: 'none' }}
                onFocus={(e) => e.target.style.backgroundColor = '#F9FAFB'}
              required
            />
            </div>
            {emailError && <span className="error-text">{emailError}</span>}
          </div>
          
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <div className="input-wrapper">
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                onBlur={handlePasswordBlur}
                placeholder="Enter your password"
                className={passwordError || error ? 'error' : ''}
                style={{ backgroundColor: '#F9FAFB', outline: 'none' }}
                onFocus={(e) => e.target.style.backgroundColor = '#F9FAFB'}
                required
              />
            </div>
            {passwordError && <span className="error-text">{passwordError}</span>}
          </div>
          
          <div className="remember-me-container">
            <label className="remember-me-label">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="remember-me-checkbox"
              />
              <span className="remember-me-text">Remember me</span>
            </label>
          </div>
          
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}
          
          <button 
            type="submit" 
            className="login-btn"
            disabled={loading}
          >
            {loading ? (
              <>
                <svg className="spinner" width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeDasharray="32" strokeDashoffset="32">
                    <animate attributeName="stroke-dasharray" dur="2s" values="0 32;16 16;0 32;0 32" repeatCount="indefinite"/>
                    <animate attributeName="stroke-dashoffset" dur="2s" values="0;-16;-32;-32" repeatCount="indefinite"/>
                  </circle>
                </svg>
                Signing in...
              </>
            ) : 'Sign In'}
          </button>
        </form>

      </div>
    </div>
  );
}

export default SchoolLogin;
